import { environment } from "@app/relay/relay-environment";
import JobCard, { type QueueJob } from "@app/components/account-queue/job-card";
import {
  accountQueueQuery,
  cancelMutation,
  enqueueMutation,
} from "@app/components/account-queue/queue-fragment";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, useLazyLoadQuery } from "react-relay";

import type { queueFragment_Query } from "@app/queries/queueFragment_Query.graphql";
import type { queueFragment_CancelMutation } from "@app/queries/queueFragment_CancelMutation.graphql";
import type { queueFragment_EnqueueMutation } from "@app/queries/queueFragment_EnqueueMutation.graphql";

// The wallet's own progress card, which is now the account queue read through a
// filter rather than a second implementation of one.
//
// This replaced ~570 lines that maintained a parallel notion of phases, waiting
// states, stall detection and stop semantics for wallet imports alone. All of it
// now lives in JobCard, which the queue page and the footer draw from too — so a
// wallet import cannot say one thing here and another thing three feet away in
// the footer, which is exactly what two implementations eventually do.
const POLL_MS = 2000;

export default function WalletPromotionProgress({ slug }: { slug: string }) {
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const data = useLazyLoadQuery<queueFragment_Query>(
    accountQueueQuery,
    { history: 10 },
    // A fetchKey bump does nothing under store-or-network; network-only is what
    // makes the poll actually reach the server.
    { fetchKey: tick, fetchPolicy: tick > 0 ? "network-only" : "store-or-network" }
  );

  const queue = data.accountQueue;
  const mine = (job: QueueJob) =>
    job.kind === "WALLET_IMPORT" &&
    (job.params as Record<string, unknown> | null)?.slug === slug;

  const active = (queue.active as ReadonlyArray<QueueJob>).filter(mine);
  const recent = (queue.history as ReadonlyArray<QueueJob>).filter(mine).slice(0, 1);
  const shown = active.length > 0 ? active : recent;
  const polling = active.length > 0;

  // Fetch first, then bump the key — see the note in queue-footer. A poll that
  // fails must leave the last good card on screen, not throw during render.
  const refresh = useCallback(
    () =>
      fetchQuery<queueFragment_Query>(
        environment,
        accountQueueQuery,
        { history: 10 },
        { fetchPolicy: "network-only" }
      )
        .toPromise()
        .then(() => setTick((n) => n + 1))
        .catch(() => {}),
    []
  );

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!polling) return;
    timer.current = setInterval(() => void refresh(), POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [polling, refresh]);

  const stop = (jobId: number) => {
    setBusy(true);
    commitMutation<queueFragment_CancelMutation>(environment, {
      mutation: cancelMutation,
      variables: { jobId },
      onCompleted: (response) => {
        setBusy(false);
        setNotice(
          response.accountJobCancel?.outcome === "stopping"
            ? "Asked it to stop — it will finish the step it is on. Press Stop again to give up waiting."
            : "Stopped."
        );
        void refresh();
      },
      onError: () => setBusy(false),
    });
  };

  const retry = (jobId: number) => {
    setBusy(true);
    commitMutation<queueFragment_EnqueueMutation>(environment, {
      mutation: enqueueMutation,
      variables: { kind: "WALLET_IMPORT", params: { slug }, retryJobId: jobId },
      onCompleted: () => {
        setBusy(false);
        void refresh();
      },
      onError: () => setBusy(false),
    });
  };

  if (shown.length === 0) return null;

  return (
    <div className="space-y-2">
      {notice && <p className="text-xs text-gray-500">{notice}</p>}
      {shown.map((job) => (
        <JobCard key={job.jobId} job={job} busy={busy} onStop={stop} onRetry={retry} />
      ))}
      <p className="text-xs text-gray-400">
        This import shares one queue with everything else on the account —{" "}
        <Link to="/queue" className="text-blue-600 hover:underline">
          see the whole queue
        </Link>
        .
      </p>
    </div>
  );
}
